package fr.manyverse.app.infrastructure.monitoring

import io.github.jan.supabase.auth.exception.AuthRestException
import io.github.jan.supabase.exceptions.RestException
import io.github.jan.supabase.postgrest.exception.PostgrestRestException
import io.ktor.client.statement.request

internal const val DB_ENDPOINT_TAG = "db.endpoint"
internal const val DB_STATUS_TAG = "db.status"
internal const val DB_CODE_TAG = "db.code"

private const val MAX_ENDPOINT_SEGMENTS = 4
private val ID_SEGMENT = Regex("""[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|\d+""")

/**
 * Tags Sentry décrivant l'erreur Supabase portée par [this] ou l'une de ses causes : endpoint
 * appelé, statut HTTP et code d'erreur (code Postgres comme `42501` pour un refus RLS, ou code
 * d'erreur d'auth comme `otp_expired`). Vide si ce n'est pas une réponse d'erreur de Supabase.
 *
 * Nécessaires au regroupement : une [RestException] est levée par supabase-kt après la reprise
 * de la coroutine, sa stack trace ne contient donc aucune frame de nos repositories. Sans
 * empreinte dédiée, Sentry range toutes les erreurs de base dans la même issue.
 */
internal fun Throwable.databaseErrorTags(): Map<String, String> {
    val restException = generateSequence(this) { it.cause }
        .filterIsInstance<RestException>()
        .firstOrNull()
        ?: return emptyMap()

    val request = restException.response.request
    val code = when (restException) {
        is PostgrestRestException -> restException.code
        is AuthRestException -> restException.error
        else -> null
    }
    return buildMap {
        put(DB_ENDPOINT_TAG, "${request.method.value} ${normalizeEndpoint(request.url.encodedPath)}")
        put(DB_STATUS_TAG, restException.statusCode.toString())
        code?.takeIf { it.isNotBlank() }?.let { put(DB_CODE_TAG, it) }
    }
}

/**
 * Réduit un chemin d'API Supabase à ce qui identifie l'opération : `/rest/v1/<table>`,
 * `/rest/v1/rpc/<fonction>`, `/auth/v1/<action>`, `/storage/v1/object/<bucket>`. Le reste du
 * chemin (fichiers Storage, identifiants) créerait une issue Sentry par ligne concernée.
 */
internal fun normalizeEndpoint(path: String): String =
    path.split('/')
        .filter { it.isNotEmpty() }
        .take(MAX_ENDPOINT_SEGMENTS)
        .joinToString(separator = "/", prefix = "/") { if (ID_SEGMENT.matches(it)) ":id" else it }

/**
 * Empreinte Sentry d'une erreur de base : une issue par endpoint, statut et code d'erreur,
 * quel que soit l'endroit du code d'où elle vient. `null` si l'événement ne porte pas de tag
 * [DB_ENDPOINT_TAG] : le regroupement par défaut de Sentry s'applique alors.
 */
internal fun databaseFingerprint(tags: Map<String, String>): List<String>? {
    val endpoint = tags[DB_ENDPOINT_TAG] ?: return null
    return listOfNotNull("supabase", endpoint, tags[DB_STATUS_TAG], tags[DB_CODE_TAG])
}
